import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { DrLocalStorageService } from '../services/local-storage/dr-local-storage.service';
import { User } from '@app/shared/models/user';
import { BaseHttpWithBundleService } from '@app/core/services/base/base-service-with-bundle.service';
import { HttpServiceBundle } from '@app/core/services/base/http-service-bundle.service';
import { ClientUser } from '@app/shared/models/client-user';
import { BackendUriUnits } from '@app/core/services/base/backend-uri-units';
import { LogicOp, Permission } from '@app/core/authentication/permission';
import { SessionType } from '@app/shared/models/session-type';


@Injectable({ providedIn: 'root' })
export class AuthenticationService extends BaseHttpWithBundleService {

    private currentUserSubject: BehaviorSubject<User>;
    public currentUser: Observable<User>;

    constructor(protected httpServiceBundle: HttpServiceBundle, protected drLocalStorageService: DrLocalStorageService) {
        super(httpServiceBundle, true);
        this.uri += `/${BackendUriUnits.CLIENTS}`;

        const storageUser = this.drLocalStorageService.get(this.drLocalStorageService.STORAGE_KEY_USER);
        this.currentUserSubject = new BehaviorSubject<User>(storageUser);
        this.currentUser = this.currentUserSubject.asObservable();
    }

    public get currentUserValue(): User {
        return this.currentUserSubject.value;
    }

    public get userIsSupportUser(): boolean {
        return this.currentUserValue.hasPermissions([Permission.SUPPORT_ADMIN, Permission.SUPPORT_USER], LogicOp.OR);
    }

    public get sessionType(): SessionType {
        return this.currentUserValue.sessionType;
    }

    public login(username: string, password: string) {
        const uri = `${this.uri}/${BackendUriUnits.LOGIN}/${BackendUriUnits.LOGIN}`;
        return this.http.post<any>(uri, {
            cred1: username,
            cred2: password,
            resumeUserSession: 'true'
        })
            .pipe(map(user => {
                if (user.potential_clients) {
                    const sessionUser = encodeURIComponent(JSON.stringify(username));
                    const sessionClients = encodeURIComponent(JSON.stringify(user.potential_clients));
                    const sessionToken = user.accessToken;
                    // DEV:
                    // const multiClientHost = 'http://dynarex.local';
                    const multiClientHost = environment.clientSelectBaseUrl;
                    const clientSelectURL = multiClientHost + `/auth/?clientSelectUser=${sessionUser}&clientSelectToken=${sessionToken}&clientSelectClients=${sessionClients}`;
                    return window.location.href = clientSelectURL;
                } else {
                    user.sessionType = SessionType.SINGLE_CLIENT;

                    return this.localStoreAsUser(user);
                }
            }));
    }

    public loginWithTwoFA(username: string, tan: string, token: string) {
        const uri = `${this.uri}/${BackendUriUnits.LOGIN}/${BackendUriUnits.MFA_LOGIN}?tan=${tan}`;
        return this.http.post<any>(uri, {
            cred1: username,
            resumeUserSession: 'true'
        }, {
            headers: {
                'Access-Token': token
            }
        })
            .pipe(map(user => {
                if (user.potential_clients) {
                    const sessionUser = encodeURIComponent(JSON.stringify(username));
                    const sessionClients = encodeURIComponent(JSON.stringify(user.potential_clients));
                    const sessionToken = user.accessToken;
                    // DEV:
                    // const multiClientHost = 'http://dynarex.local';
                    const multiClientHost = environment.clientSelectBaseUrl;
                    const clientSelectURL = multiClientHost + `/auth/?clientSelectUser=${sessionUser}&clientSelectToken=${sessionToken}&clientSelectClients=${sessionClients}`;
                    return window.location.href = clientSelectURL;
                } else {
                    user.sessionType = SessionType.SINGLE_CLIENT;

                    return this.localStoreAsUser(user);
                }
            }));
    }

    public loginSupportUser(supportUser: string, supportClient: string, supportToken: string, supportCountry: string) {
        return new Observable((observer) => {
            const data = {
                accessToken: supportToken,
                client: JSON.parse(supportClient),
                user: JSON.parse(supportUser),
                country: JSON.parse(supportCountry),
                sessionType: SessionType.SUPPORT
            };

            const user = this.localStoreAsUser(data);

            if (user) {
                observer.next(user);
                observer.complete();
            } else {
                throw throwError('Invalid user structure');
            }

            return () => {};
        });
    }

    public loginToClient(token: string, clientId: string, username: string) {
        const uri = `${this.uri}/${BackendUriUnits.LOGIN}/${BackendUriUnits.MULTI_LOGIN}`;
        return this.http.post<any>(uri, {
            client_id: clientId,
            cred1: username,
            resumeUserSession: 'true'
        }, {
            headers: {
                'Access-Token': token
            }
        })
            .pipe(map(user => {
                user.isMultiClientUser = true;
                user.sessionType = SessionType.MULTI_CLIENT;

                return this.localStoreAsUser(user);
            }));
    }

    public verifyCredentials(password: string) {
        const uri = `${this.uri}/${BackendUriUnits.LOGIN}/${BackendUriUnits.VERIFY_LOGIN}`;
        return this.http.post<any>(uri, {
            cred1: this.currentUserValue.user.email,
            cred2: password
        })
            .pipe(map(result => result));
    }

    protected localStoreAsUser(jsonUser: any): User {
        if (jsonUser && jsonUser.accessToken) {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            const user = new User(jsonUser);
            this.drLocalStorageService.set(this.drLocalStorageService.STORAGE_KEY_USER, user);
            this.currentUserSubject.next(user);
            return user;
        }
    }
    
    public logoutUser() {
        // remove user from local storage to log user out
        const uri = `${this.uri}/${BackendUriUnits.LOGIN}/logoff`;
        return this.http.put<any>(uri, {})
                        .pipe(map(result => {
            if (result && !result.user) {
                result.user = this.currentUserValue.user.email;
            }

            return result;
        }));
    }

    public logoutSupportUser(): Observable<void> {
        const uri = `${this.uri}/support_logout`;
        return this.http.post<any>(uri, {})
                        .pipe(map(result => {
            return result.data;
        }));
    }

    public onLogoutSuccess(): void {
        this.currentUserSubject.next(null);
        this.removeUserToken();
    }

    public removeUserToken(): void {
        this.drLocalStorageService.remove(this.drLocalStorageService.STORAGE_KEY_USER);
    }

    public logoutClientUser(user: ClientUser): Observable<boolean> {
        const uri = `${this.uri}/${BackendUriUnits.LOGIN}/logoff/users/${user.id}`;
        return this.http.put<any>(uri, {})
                        .pipe(map(result => !!result));
    }
    
    public unlockUser(processId: string): Observable<boolean> {
        const uri = `${this.uri}/${BackendUriUnits.LOGIN}/unlockAccount`;
        return this.http.put<any>(uri, { processId: processId })
                        .pipe(map(result => !!result));
    }

    public changeUserPassword(processId: string, password: string): Observable<boolean> {
        const uri = `${this.uri}/${BackendUriUnits.LOGIN}/changePassword`;
        return this.http.put<any>(uri, { processId: processId, password: password })
                        .pipe(map(result => !!result));
    }

    public recoverPassword(userName: string): Observable<boolean> {
        const uri = `${this.uri}/${BackendUriUnits.LOGIN}/recovery`;
        return this.http.post<any>(uri, { email: userName })
                        .pipe(map(result => result));
    }
}
